import { ReactElement, useEffect, useState } from 'react';

import TextField from '@mui/material/TextField';

interface EventDayNumberFieldProps {
  /** The value in effect, shown in the field. */
  value: number;
  min: number;
  max: number;
  /** Visible label; omit when the field sits inside a sentence (then give an ariaLabel). */
  label?: string;
  ariaLabel?: string;
  /** Called with a whole number in range when the admin leaves the field or presses Enter. */
  onCommit: (value: number) => void;
}

/**
 * A small whole-number field for a day setting (minutes between tee times, a shotgun's first hole).
 * Saves when the admin leaves it or presses Enter, not on every keystroke; an empty or out-of-range
 * entry goes back to the value in effect.
 */
export default function EventDayNumberField({ value, min, max, label, ariaLabel, onCommit }: EventDayNumberFieldProps): ReactElement {
  const [text, setText] = useState(String(value));
  useEffect(() => setText(String(value)), [value]);

  const commit = () => {
    const n = Number(text);
    if (!Number.isInteger(n) || n < min || n > max) {
      setText(String(value));
      return;
    }
    if (n !== value) onCommit(n);
  };

  return (
    <TextField
      size="small"
      type="number"
      label={label}
      value={text}
      onChange={({ target }) => setText(target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
      slotProps={{ htmlInput: { min, max, 'aria-label': ariaLabel ?? label } }}
      sx={{ width: label ? '9rem' : '4.5rem' }}
    />
  );
}
